import React, { useState, useRef, useCallback, useEffect } from 'react';
import { renderIcon } from '../../../utils/renderIcon';
import IconPicker from '../../../../shared/IconPicker';
import {
  useRelativeTimeTick,
} from '../../../../shared/CardComponents/DueDateDisplay';
import { formatRelativeTime } from '../../../../shared/CardComponents/DueDateDisplay/utils/formatRelativeTime';

/**
 * TitleArea Component
 * 
 * Notion-style title area with:
 * - Clickable icon with picker
 * - Editable title (click to edit)
 * - Created/Updated timestamps
 */
const TitleArea = ({
  taskId,
  taskName,
  taskIcon,
  task,
  onRenameTask,
  onIconChange,
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [showIconPicker, setShowIconPicker] = useState(false);
  const titleInputRef = useRef(null);
  const titleSpanRef = useRef(null);
  const clickCursorPosRef = useRef(null);
  const iconButtonRef = useRef(null);

  useRelativeTimeTick();

  // Focus title input when editing - place cursor at click position or end of text
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      const rafId = requestAnimationFrame(() => {
        if (titleInputRef.current) {
          titleInputRef.current.focus();
          const len = titleInputRef.current.value.length;
          if (clickCursorPosRef.current !== null) {
            const pos = clickCursorPosRef.current;
            titleInputRef.current.setSelectionRange(pos, pos);
          } else {
            titleInputRef.current.setSelectionRange(len, len);
          }
          clickCursorPosRef.current = null;
        }
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [isEditingTitle]);

  // Title editing handlers
  const handleTitleClick = useCallback((e) => {
    if (titleSpanRef.current && window.getSelection) {
      const range = document.caretRangeFromPoint(e.clientX, e.clientY);
      if (range && titleSpanRef.current.contains(range.startContainer)) {
        clickCursorPosRef.current = range.startOffset;
      } else {
        clickCursorPosRef.current = (taskName || '').length;
      }
    } else {
      clickCursorPosRef.current = (taskName || '').length;
    }
    
    setEditedTitle(taskName || '');
    setIsEditingTitle(true);
  }, [taskName]);

  const handleTitleBlur = useCallback(() => {
    if (editedTitle.trim() && editedTitle !== taskName) {
      onRenameTask?.(taskId, editedTitle.trim());
    }
    setIsEditingTitle(false);
  }, [editedTitle, taskId, taskName, onRenameTask]);

  const handleTitleKeyDown = useCallback((e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleBlur();
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false);
    }
  }, [handleTitleBlur]);

  // Icon change handler
  const handleIconSelect = useCallback((icon) => {
    onIconChange?.(taskId, icon);
    setShowIconPicker(false);
  }, [taskId, onIconChange]);

  return (
    <div className="mfv-title-area">
      {/* Icon button - vertically centered to title + timestamps */}
      <button 
        ref={iconButtonRef}
        className={`mfv-title-area__icon ${taskIcon ? '' : 'mfv-title-area__icon--empty'}`}
        onClick={() => setShowIconPicker(true)}
        title={taskIcon ? "Change icon" : "Add icon"}
      >
        {taskIcon ? (
          renderIcon(taskIcon, { size: '48px' })
        ) : (
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="16" />
            <line x1="8" y1="12" x2="16" y2="12" />
          </svg>
        )}
      </button>
      
      {/* Icon picker */}
      {showIconPicker && (
        <IconPicker
          currentIcon={taskIcon}
          onIconChange={handleIconSelect}
          onIconDeleted={() => handleIconSelect(null)}
          onClose={() => setShowIconPicker(false)}
          position={
            iconButtonRef.current
              ? {
                  x: iconButtonRef.current.getBoundingClientRect().left,
                  y: iconButtonRef.current.getBoundingClientRect().bottom + 8,
                }
              : { x: 100, y: 100 }
          }
        />
      )}
      
      {/* Title + timestamps block */}
      <div className="mfv-title-area__text-block">
        {/* Title */}
        {isEditingTitle ? (
          <textarea
            ref={titleInputRef}
            className="mfv-title-area__input"
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
            onBlur={handleTitleBlur}
            onKeyDown={handleTitleKeyDown}
            rows={1}
          />
        ) : (
          <h1 
            ref={titleSpanRef}
            className="mfv-title-area__title"
            onClick={handleTitleClick}
            title="Click to edit"
          >
            {taskName}
          </h1>
        )}
        
        {/* Timestamps - Created & Last Updated */}
        {task && (
          <div className="mfv-title-area__timestamps">
            {task.createdAt && (
              <span className="mfv-title-area__timestamp" title={new Date(task.createdAt).toLocaleString()}>
                Created: {formatRelativeTime(task.createdAt)}
              </span>
            )}
            {task.createdAt && task.updatedAt && (
              <span className="mfv-title-area__timestamp-sep">•</span>
            )}
            {task.updatedAt && (
              <span className="mfv-title-area__timestamp" title={new Date(task.updatedAt).toLocaleString()}>
                Last Updated: {formatRelativeTime(task.updatedAt)}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TitleArea;
